import { type ReactNode, useId, useState } from 'react'

interface CardProps {
  /** Whatever titles the card: a section heading, or a child's avatar and name. */
  header?: ReactNode
  /** Inside one of the section cards: a border in place of the shadow. */
  nested?: boolean
  children: ReactNode
  testId?: string
}

/**
 * The portal's panel. A section is a white card with the shadow every card
 * in the bundle shares; a child inside the family section is a bordered card
 * within it. Both take the same header, and usually a stack of `Rows`.
 */
export function Card({ header, nested = false, children, testId }: CardProps) {
  return (
    <section
      className={nested ? 'rounded-2xl border border-black/10' : 'rounded-card bg-white shadow-xl'}
      data-testid={testId}
    >
      {header && <header className="border-b border-black/5 px-6 py-5">{header}</header>}
      {children}
    </section>
  )
}

/**
 * A card's usual body: setting rows, a hairline between each. A card with
 * more than one stack names each, so a parent can tell the subscription rows
 * from the account ones.
 */
export function Rows({ heading, children }: { heading?: string; children: ReactNode }) {
  return (
    <div className="px-6 py-2">
      {heading && <h4 className="pt-3 text-sm font-semibold text-ink">{heading}</h4>}
      <div className="divide-y divide-black/5">{children}</div>
    </div>
  )
}

/**
 * Rows a parent opens only when they need them, behind their heading. They
 * start closed, and stay mounted while closed, so an edit in progress
 * survives closing them.
 */
export function CollapsibleRows({
  heading,
  children,
  testId,
}: {
  heading: string
  children: ReactNode
  testId: string
}) {
  const [open, setOpen] = useState(false)
  const id = useId()

  return (
    <div className="px-6 py-2" data-testid={testId}>
      <h4 className="pt-3 pb-1 text-sm font-semibold text-ink">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen(!open)}
          className="flex w-full items-center justify-between gap-4 rounded-field text-left outline-none transition hover:text-brand focus-visible:ring-2 focus-visible:ring-brand-ring"
          data-testid={`${testId}-toggle`}
        >
          {heading}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
            className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <path
              d="m4 6 4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </h4>
      <div id={id} hidden={!open} className="divide-y divide-black/5">
        {children}
      </div>
    </div>
  )
}
